/**
 * VUKZ Valuation — Derived from financial scenarios + assumptions
 * Source of truth for all valuation displays (Financeiro + Investidores)
 */
import { FINANCIAL_SCENARIOS, INVESTMENT_SCENARIOS } from '../../pages/governance/commercial/financialScenariosData';
import { COMMERCIAL_PLANS } from '../../pages/governance/commercial/commercialPlansData';
import { FINANCIAL_ASSUMPTIONS, COMPANY_INFO } from './assumptions';

// ─── Valuation Methods ──────────────────────────────────────────────────────

export const VALUATION_METHODS = [
  {
    id: 'replacement-cost',
    name: 'Custo de Reposição',
    description: 'Quanto custaria reconstruir toda a tecnologia do zero',
    value: 5700000, // R$ 5.7M (com fator 1.5x produção)
    rawCost: 3800000, // R$ 3.8M sem fator
    multiplier: 1.5,
    details: [
      { component: 'AI Pipeline (14 agents)', devMonths: 24, cost: 1500000 },
      { component: 'TrackVision (CV multi-sport)', devMonths: 12, cost: 800000 },
      { component: 'SwimVision (biomecânica)', devMonths: 8, cost: 600000 },
      { component: 'Social Feed Engine', devMonths: 6, cost: 400000 },
      { component: 'SaaS Control Plane', devMonths: 4, cost: 300000 },
      { component: 'Identity Resolution', devMonths: 3, cost: 200000 },
    ],
  },
  {
    id: 'revenue-multiple',
    name: 'Múltiplo de Receita (ARR)',
    description: 'Valuation = ARR × múltiplo do setor',
    scenarios: [
      { label: 'Conservador (Bootstrap Y5)', arr: 4500000, multiple: 8, valuation: 36000000 },
      { label: 'Base (Seed Y5)', arr: 13200000, multiple: 10, valuation: 132000000 },
      { label: 'Otimista (Seed+A Y5)', arr: 30000000, multiple: 12, valuation: 360000000 },
    ],
  },
  {
    id: 'comparable',
    name: 'Comparáveis de Mercado',
    description: 'Referências de valuations em sports tech',
    comparables: [
      { company: 'Hudl', valuation: 4000000000, stage: 'Late (15+ anos)', note: 'Video analysis US' },
      { company: 'Catapult Sports', valuation: 1300000000, stage: 'Late (IPO)', note: 'Wearables + analytics' },
      { company: 'Stats Perform/Opta', valuation: 800000000, stage: 'Acquired', note: 'Sports data' },
      { company: 'Metrica Sports', valuation: 30000000, stage: 'Series A', note: 'Tracking video' },
      { company: 'VUKZ.ai (pre-revenue)', valuation: 12000000, stage: 'Seed', note: 'Tecnologia + IP + mercado' },
    ],
  },
];

// ─── Current Valuation Snapshot ─────────────────────────────────────────────

export const CURRENT_VALUATION = {
  preMoneyBRL: 11700000, // R$ 11.7M
  preMoneyUSD: 11700000 / FINANCIAL_ASSUMPTIONS.fxRate, // ~USD 2.25M
  method: 'Custo de reposição (1.5x) + potencial de mercado + IP diferenciado',
  justification: [
    'R$ 3.8M em custo de desenvolvimento (108 dev-months equivalentes)',
    'Fator 1.5x por estar em produção com dados reais',
    'IP diferenciado: visão computacional + 35 AI agents + identity resolution',
    'Mercado endereçável de R$ 80M SAM no Brasil',
    'Microsoft for Startups ($250K Azure credits) validando a tese',
    'Potencial de venda de AI agents no Microsoft Marketplace',
  ],
  stage: 'Pre-Seed → Seed (buscando R$ 1.3M)',
};

// ─── Cap Table ──────────────────────────────────────────────────────────────

export const CAP_TABLE = {
  preSeed: [
    { shareholder: 'Paulo Brant (Founder & CEO)', class: 'Common A', ownership: 100, shares: 10000000 },
  ],
  postSeed: [
    { shareholder: 'Paulo Brant (Founder & CEO)', class: 'Common A', ownership: 82, shares: 10000000 },
    { shareholder: 'Seed Investors', class: 'Preferred', ownership: 10, shares: 1220000 },
    { shareholder: 'ESOP Pool', class: 'Options', ownership: 8, shares: 976000 },
  ],
  postSeriesA: [
    { shareholder: 'Paulo Brant (Founder & CEO)', class: 'Common A', ownership: 58, shares: 10000000 },
    { shareholder: 'Seed Investors', class: 'Preferred Seed', ownership: 10, shares: 1220000 },
    { shareholder: 'Series A Investors', class: 'Preferred A', ownership: 20, shares: 3450000 },
    { shareholder: 'ESOP Pool', class: 'Options', ownership: 12, shares: 2070000 },
  ],
  notes: [
    'Empresa constituída como LTDA (Brasil)',
    'Conversão para S/A planejada pré-Seed',
    'ESOP: 8-12% reservado para CTO + primeiros 5 hires',
    'Vesting padrão: 4 anos, cliff de 1 ano',
    'Anti-dilution: weighted average (standard para BR)',
  ],
};

// ─── Build dynamic valuation from scenarios ─────────────────────────────────

export function getValuationByScenario(scenarioId) {
  const scenario = FINANCIAL_SCENARIOS.find(s => s.id === scenarioId);
  if (!scenario) return null;
  
  const lastYear = scenario.yearly[scenario.yearly.length - 1];
  const arr = lastYear.arr;
  const multiple = arr > 3000000 ? 12 : arr > 1000000 ? 10 : 8;
  
  return {
    scenarioId,
    label: scenario.label,
    arrY5: arr,
    multiple,
    impliedValuation: arr * multiple,
    investment: INVESTMENT_SCENARIOS[scenario.investment],
  };
}

export function getAllValuationScenarios() {
  return FINANCIAL_SCENARIOS.map(s => getValuationByScenario(s.id));
}

// ─── Use of Funds ───────────────────────────────────────────────────────────

export const USE_OF_FUNDS = {
  seed: {
    total: 1300000,
    allocation: [
      { category: 'Equipe (2 devs + 1 comercial)', amount: 600000, pct: 46 },
      { category: 'Marketing & Aquisição', amount: 250000, pct: 19 },
      { category: 'GPU/Infra (TrackVision + SwimVision)', amount: 200000, pct: 15 },
      { category: 'Novos esportes + Pipeline Academia', amount: 150000, pct: 12 },
      { category: 'Reserva operacional', amount: 100000, pct: 8 },
    ],
  },
  seriesA: {
    total: 8000000,
    allocation: [
      { category: 'Equipe (eng + sales + CS + produto)', amount: 3000000, pct: 37 },
      { category: 'Go-to-market (federações, eventos, inside sales)', amount: 2000000, pct: 25 },
      { category: 'Produto (GPU, novos esportes, academia)', amount: 1500000, pct: 19 },
      { category: 'LATAM (Argentina, Chile)', amount: 1000000, pct: 13 },
      { category: 'Reserva', amount: 500000, pct: 6 },
    ],
  },
};
